// main.ts — vite-ssg 标准入口

import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import { routes } from './router'
import { routerHolder } from './router/instance'
import App from './App.vue'

import './assets/styles/tokens.css'
import { i18n } from './i18n'
import { useLocaleStore } from './stores/locale'
import { captureInviteFromQuery } from './composables/useInviteCode'
import { DEFAULT_LOCALE } from './config'

export const createApp = ViteSSG(
  App,
  {
    routes,
    // 滚动行为：
    // 1. savedPosition（浏览器前进/后退）→ 恢复原位
    // 2. to.hash（外部深链 / 原生 <a href="#x">）→ 滚到锚点
    // 3. 其他 → 瞬时滚到顶部
    //
    // 行为 3 之前返回 false（Vue Router 4 意为「不动 scroll」），导致
    // 跳到 /about /privacy /terms 等无 hash 页面时继承上一页滚动位置
    // ——用户感知为「自动滚到页面底部」或「停在上一页锚点处」。
    //
    // 副作用：useAnchorNav 跨页 hash 流程（/privacy → /#features）走的是
    // path 切换不传 hash，scrollBehavior 现在看得见，会先瞬时滚到顶部，
    // 然后 useAnchorNav 自己 smoothScrollTo 到 #features。视觉上
    // 「新 home 顶部 → 平滑滑到 features」，比之前「从 /privacy 滚动
    // 位置跳到 features」更可预测。
    // 见 docs/mainadr/0008-anchor-navigation.md（phantom，未实现）
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) return savedPosition
      if (to.hash) {
        return { el: to.hash, behavior: 'auto' as const }
      }
      return { top: 0, left: 0, behavior: 'auto' as const }
    }
  },
  ({ app, router, isClient, head }) => {
    const pinia = createPinia()
    app.use(pinia)
    app.use(i18n)
    // 暴露 router 实例给非组件模块（api/siteClient 登录态失效跳转用）
    routerHolder.instance = router
    // vite-ssg 已经 app.use(head) 过；这里直接用 ctx.head 即可
    void head

    // client-only：恒挂 dark class（深色唯一）+ hydrate locale
    if (isClient) {
      // 所有站内导航自动携带 lang query(界面语言跨页通用,默认 zh-CN)
      // 注：locale store current 初始值由 i18n 模块按 URL ?lang= > localStorage > 浏览器语言推断,
      // 因此导航时读取到的就是当前实际界面语言,目标已带相同 lang 时直接放行,不会重定向循环。
      router.beforeEach((to) => {
        // 邀请码捕获（防丢失）：任一页面 URL 带 ?invite= 即持久化到 localStorage
        // 全局捕获，用户从邀请链接进入后浏览任意页面都不丢
        captureInviteFromQuery(to.query.invite as string | undefined)

        const lang = useLocaleStore().current || DEFAULT_LOCALE
        if (to.query.lang !== lang) {
          return { ...to, query: { ...to.query, lang } }
        }
        return true
      })
      router.isReady().then(() => {
        document.documentElement.classList.add('dark')
        const localeStore = useLocaleStore()
        localeStore.hydrate()
      })
    }
  }
)
